import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store/store";
import { removeUnderscores, validateString } from "../../../utils/helpers/ObjectCheckerHelper";
import { parseJwt } from "../../../utils/helpers/WindowUtils";
import { ServiceProvider } from "../../../api/models/service-provider";
import { CpmUser } from "../../../api/models/cpm-user";
import { CpmMetadata } from "../../../api/models/cpm-metadata";
import { LinkedDocumentInfo } from "../../../api/models/linked-document-info";
import { IxbrlSetting } from "../../../api/models/ixbrl-setting";
import { UserDocumentCheckout } from "../../../api/models/user-document-checkout";

// GLOBAL MODELS AND BUSINESS FUNCTIONS
export interface TenantConfiguration {
  name: string;
  url: string; // tenantid.co2.sastagetik,co
  tenantId: string; //TEMP
  idpUrl: string; //TEMP ;----
  idpClientId: string; //TEMP ;----
  authData?: any; //TODO understand the needed type
  cpmUser?: CpmUser;
  cpmMetadata?: CpmMetadata;
}

export interface TenantConfigurationSettings {
  sharePointTenantId: string;
  sharePointHostName: string;
  sharePointSite: string;
  tenantId: string;
  cpmUrl: string;
  cpmCustomerName: string;
  idpAppRegistrationId: string;
  environmentInfo?: TenantConfigurationEnvironmentInfo;
}

export interface TenantConfigurationEnvironmentInfo {
  idpUrl: string;
  tagetikUrl: string;
}

export function formatIdpUrl(tenantId: string): string {
  return validateString(tenantId) ? `https://${removeUnderscores(tenantId)}.idp.saastagetik.com/` : "";
}

export function formatUrl(tenantId: string): string {
  return validateString(tenantId) ? `https://${removeUnderscores(tenantId)}.co2.saastagetik.com/co-service` : "";
}

export interface UpdatedTenantConfiguration {
  tenantConfiguration: TenantConfiguration[];
  index: number;
}

export interface TenantConfigurationValidity {
  valid: boolean;
  propertyValidationEntries: PropertyValidation[];
}

interface ConfigurationsState {
  configurations: TenantConfiguration[];
  dirty: boolean[];
  edit: boolean[];
  validations: TenantConfigurationValidity[];
  selected: string;
  documentTenantId: string;
  clientId: string;
  protectionType: number;
  seahorseDocumentLinked: LinkedDocumentInfo[];
  serviceProvider?: ServiceProvider;
  ixbrlSettings?: IxbrlSetting[];
  ixbrlEnabled?: boolean;
  inDesignDocumentLinked: LinkedDocumentInfo[];
  userDocumentCheckout?: UserDocumentCheckout[];
  allUserDocumentCheckout?: UserDocumentCheckout[];
  isSendingIxbrl?: boolean;
}

function validateConfiguration(config: TenantConfiguration): TenantConfigurationValidity {
  let valid = true;

  // mandatory message is not showed because we have to mark yet
  if (!validateString(config.name)) {
    valid = false;
  }
  if (!validateString(config.tenantId)) {
    valid = false;
  }
  if (!validateString(config.idpClientId)) {
    valid = false;
  }

  return {
    valid: valid,
    propertyValidationEntries: [],
  };
}

function validateConfigurations(configurations: TenantConfiguration[]): TenantConfigurationValidity[] {
  const validations: TenantConfigurationValidity[] = [];
  const nameMap = new Map<string, number[]>();
  const tenantIdMap = new Map<string, number[]>();

  configurations.forEach((conf, index) => {
    const confValidity = validateConfiguration(conf);
    validations.push(confValidity);
    // check duplicate config with name
    const configIndexesWithName = [...(nameMap.get(conf.name) || []), index];
    nameMap.set(conf.name, configIndexesWithName);
    if (configIndexesWithName.length > 1) {
      configIndexesWithName.forEach((i) => {
        validations[i].valid = false;
        validations[i].propertyValidationEntries.unshift({
          propertyName: "name",
          message: "cof.base.info.displaynameunique",
        });
      });
    }
    // check duplicate config with tenantID
    const configIndexesWithTenantId = [...(tenantIdMap.get(conf.tenantId) || []), index];
    tenantIdMap.set(conf.tenantId, configIndexesWithTenantId);
    if (configIndexesWithTenantId.length > 1) {
      configIndexesWithTenantId.forEach((i) => {
        validations[i].valid = false;
        validations[i].propertyValidationEntries.push({
          propertyName: "tenantId",
          message: "cof.base.tenantidmustbeunique",
        });
      });
    }
  });

  return validations;
}

export function isDirtyState(confState: ConfigurationsState): boolean {
  return confState.dirty.reduce((someDirty, dirty) => someDirty || dirty, false);
}

export function isDirtyStateOtherIndex(confState: ConfigurationsState, index: number): boolean {
  let newVar: boolean[] = [...confState.dirty.slice(0, index), ...confState.dirty.slice(index + 1)];
  return newVar.reduce((someDirty, dirty) => someDirty || dirty, false);
}

export function isValidState(confState: ConfigurationsState): boolean {
  return confState.validations.reduce((valid, val) => valid && val.valid, true);
}

export function activeConfiguration(confState: ConfigurationsState): TenantConfiguration {
  if (confState === undefined) {
    return null;
  }
  if (confState.configurations.length === 0) {
    return null;
  }
  if (confState.selected == null || confState.selected == "") {
    if (confState.documentTenantId == null || confState.documentTenantId == "") {
      return null;
    } else {
      const configByDoc = confState.configurations.find((value) => value.tenantId == confState.documentTenantId);
      if (configByDoc != null) {
        selectConfiguration(configByDoc.name);
        return configByDoc;
      }
    }
  }
  return confState.configurations.find((value) => value.name == confState.selected);
}

// LOCAL STORAGE MODELS
export interface PropertyValidation {
  propertyName: string;
  message: string;
}

interface StoredConfiguration {
  configurations: TenantConfiguration[];
}

const STORAGE_KEY = "CO2_CONFIGURATIONS";

function loadFromLocalStorage(): StoredConfiguration {
  return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{"configurations": []}') as StoredConfiguration;
}

function saveToLocalStorage(configurations: TenantConfiguration[]) {
  const storedConfiguration: StoredConfiguration = {
    configurations: JSON.parse(JSON.stringify(configurations)),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(storedConfiguration));
}

const initialConfigurations = loadFromLocalStorage().configurations;
const initialState: ConfigurationsState = {
  configurations: initialConfigurations,
  edit: new Array(initialConfigurations.length).fill(false),
  dirty: new Array(initialConfigurations.length).fill(false),
  validations: validateConfigurations(initialConfigurations),
  selected: "",
  documentTenantId: "",
  clientId: "",
  protectionType: 0,
  serviceProvider: undefined,
  seahorseDocumentLinked: undefined,
  ixbrlSettings: [],
  ixbrlEnabled: false,
  inDesignDocumentLinked: undefined,
  userDocumentCheckout: [],
  allUserDocumentCheckout: [],
  isSendingIxbrl: false,
};

export interface UpdateTenantAuthDataPayload {
  tenantId: string;
  authData: any;
}

const slice = createSlice({
  name: "configurations",
  initialState,
  reducers: {
    updateConfigurationsState: (state, action: PayloadAction<UpdatedTenantConfiguration>) => {
      state.configurations = action.payload.tenantConfiguration;
      state.validations = validateConfigurations(state.configurations);
      state.dirty = new Array(state.configurations.length).fill(false);
      state.edit = new Array(state.configurations.length).fill(false);
      state.dirty[action.payload.index] = true;
      state.edit[action.payload.index] = true;
    },
    confirmConfigurationsState: (state, action: PayloadAction<number>) => {
      if (isValidState(state)) {
        const storedConfigs = loadFromLocalStorage();
        let index = action.payload.valueOf();
        if (storedConfigs.configurations[index]?.name == state.selected) {
          state.selected = "";
        }
        const configurations = [
          ...storedConfigs.configurations.slice(0, index),
          state.configurations[index],
          ...storedConfigs.configurations.slice(index + 1),
        ];
        saveToLocalStorage(configurations);
        state.dirty = new Array(state.configurations.length).fill(false);
        state.edit = new Array(state.configurations.length).fill(false);
      }
    },
    addNewConfiguration: (state) => {
      state.configurations = [
        ...state.configurations,
        {
          name: "",
          url: "",
          tenantId: "",
          idpUrl: "",
          idpClientId: "",
        },
      ];
      state.dirty = new Array(state.configurations.length).fill(false);
      state.edit = new Array(state.configurations.length).fill(false);
      state.dirty[state.configurations.length - 1] = true;
      state.edit[state.configurations.length - 1] = true;
      state.validations = validateConfigurations(state.configurations);
    },
    bulkImportConfigurations: (state, action: PayloadAction<Array<TenantConfigurationSettings & { name: string }>>) => {
      const storedConfigs = loadFromLocalStorage();
      const newConfigs: TenantConfiguration[] = action.payload.map((parsedConf) => ({
        name: parsedConf.name ?? "",
        url: parsedConf.environmentInfo?.tagetikUrl
          ? parsedConf.environmentInfo.tagetikUrl
          : formatUrl(parsedConf.tenantId),
        tenantId: parsedConf.tenantId,
        idpUrl: parsedConf.environmentInfo?.idpUrl
          ? parsedConf.environmentInfo.idpUrl
          : formatIdpUrl(parsedConf.tenantId),
        idpClientId: parsedConf.idpAppRegistrationId,
      }));
      const configurations = [...storedConfigs.configurations, ...newConfigs];
      saveToLocalStorage(configurations);
      state.configurations = configurations;
      state.dirty = new Array(configurations.length).fill(false);
      state.edit = new Array(configurations.length).fill(false);
      state.validations = validateConfigurations(configurations);
    },
    pasteNewConfiguration: (state, text: PayloadAction<TenantConfigurationSettings>) => {
      let parsedConf: TenantConfigurationSettings;
      parsedConf = text.payload;
      state.configurations = [
        ...state.configurations,
        {
          name: "",
          url: parsedConf.environmentInfo.tagetikUrl
            ? parsedConf.environmentInfo.tagetikUrl
            : formatUrl(parsedConf.tenantId),
          tenantId: parsedConf.tenantId,
          idpUrl: parsedConf.environmentInfo.idpUrl
            ? parsedConf.environmentInfo.idpUrl
            : formatIdpUrl(parsedConf.tenantId),
          idpClientId: parsedConf.idpAppRegistrationId,
        },
      ];
      state.dirty = new Array(state.configurations.length).fill(false);
      state.edit = new Array(state.configurations.length).fill(false);
      state.dirty[state.configurations.length - 1] = true;
      state.edit[state.configurations.length - 1] = true;
      state.validations = validateConfigurations(state.configurations);
    },
    deleteConfiguration: (state, action: PayloadAction<number>) => {
      const storedConfigs = loadFromLocalStorage();
      if (action.payload < storedConfigs.configurations.length) {
        if (storedConfigs.configurations[action.payload].name == state.selected) {
          state.selected = "";
        }
        state.configurations = [
          ...storedConfigs.configurations.slice(0, action.payload),
          ...storedConfigs.configurations.slice(action.payload + 1),
        ];
        saveToLocalStorage(state.configurations);
      } else {
        state.configurations = storedConfigs.configurations;
      }
      state.dirty = new Array(state.configurations.length).fill(false);
      state.edit = new Array(state.configurations.length).fill(false);
      state.validations = validateConfigurations(state.configurations);
    },
    resetSate: (state) => {
      const storedConfigs = loadFromLocalStorage();
      state.configurations = storedConfigs.configurations;
      state.dirty = new Array(state.configurations.length).fill(false);
      state.edit = new Array(state.configurations.length).fill(false);
      state.validations = validateConfigurations(state.configurations);
    },
    updateAuthDataForTenantConf: (state, action: PayloadAction<UpdateTenantAuthDataPayload>) => {
      const tenantId = action.payload.tenantId;
      const configAndIndex = tenantConfiguration(state, tenantId);

      if (configAndIndex) {
        //fix to handle case where sub in token is not the same as in profile
        if (action.payload.authData) {
          const token = action.payload.authData.access_token;
          action.payload.authData.profile.sub = parseJwt(token).sub;
        }
        const configurations = [
          ...state.configurations.slice(0, configAndIndex.index),
          { ...state.configurations[configAndIndex.index], authData: action.payload.authData },
          ...state.configurations.slice(configAndIndex.index + 1),
        ];
        saveToLocalStorage(configurations);
        state.configurations = configurations;
      }
    },
    setEdit: (state, action: PayloadAction<number>) => {
      const storedConfigs = loadFromLocalStorage();
      state.configurations = storedConfigs.configurations;
      state.dirty = new Array(state.configurations.length).fill(false);
      state.edit = new Array(state.configurations.length).fill(false);
      state.edit[action.payload.valueOf()] = true;
      state.validations = validateConfigurations(state.configurations);
    },
    selectConfiguration: (state, action: PayloadAction<string>) => {
      state.selected = action.payload;
    },
    setDocumentTenantId: (state, action: PayloadAction<string>) => {
      state.documentTenantId = action.payload;
      state.selected = state.configurations.find((value) => value.tenantId == action.payload)?.name;
    },
    refreshConfigurations: (state) => {
      //TEMP to fix broken ChooseConfigPane
      state.configurations = [...state.configurations];
    },
    setClientId: (state, action: PayloadAction<string>) => {
      state.clientId = action.payload;
    },
    setProtectionType: (state, action: PayloadAction<number>) => {
      state.protectionType = action.payload;
    },
    setServiceProvider: (state, action: PayloadAction<ServiceProvider>) => {
      state.serviceProvider = action.payload;
    },
    setSeahorseDocumentLinked: (state, action: PayloadAction<LinkedDocumentInfo[]>) => {
      state.seahorseDocumentLinked = action.payload;
    },
    setIxbrlSettings: (state, action: PayloadAction<IxbrlSetting[]>) => {
      state.ixbrlSettings = action.payload;
    },
    setIxbrlEnabled: (state, action: PayloadAction<boolean>) => {
      state.ixbrlEnabled = action.payload;
    },
    setInDesignDocumentLinked: (state, action: PayloadAction<LinkedDocumentInfo[]>) => {
      state.inDesignDocumentLinked = action.payload;
    },
    setUserDocumentCheckout: (state, action: PayloadAction<UserDocumentCheckout[]>) => {
      state.userDocumentCheckout = action.payload;
    },
    setAllUserDocumentCheckout: (state, action: PayloadAction<UserDocumentCheckout[]>) => {
      state.allUserDocumentCheckout = action.payload;
    },
    setIsSendingIxbrl: (state, action: PayloadAction<boolean>) => {
      state.isSendingIxbrl = action.payload;
    },
    setCpmUser: (state, action: PayloadAction<CpmUser>) => {
      const configAndIndex = tenantConfiguration(state, action.payload.tenantId);
      state.configurations[configAndIndex.index].cpmUser = action.payload;
      const configurations = [
        ...state.configurations.slice(0, configAndIndex.index),
        { ...state.configurations[configAndIndex.index], cpmUser: action.payload },
        ...state.configurations.slice(configAndIndex.index + 1),
      ];
      saveToLocalStorage(configurations);
      state.configurations = configurations;
    },
    resetCpmUser: (state, action: PayloadAction<string>) => {
      const configAndIndex = tenantConfiguration(state, action.payload);
      state.configurations[configAndIndex.index].cpmUser = undefined;
      const configurations = [
        ...state.configurations.slice(0, configAndIndex.index),
        { ...state.configurations[configAndIndex.index], cpmUser: undefined },
        ...state.configurations.slice(configAndIndex.index + 1),
      ];
      saveToLocalStorage(configurations);
    },
    setCpmMetadata: (state, action: PayloadAction<{ metadata: CpmMetadata; tenantId: string }>) => {
      const configAndIndex = tenantConfiguration(state, action.payload.tenantId);
      if (configAndIndex) {
        state.configurations[configAndIndex.index].cpmMetadata = action.payload.metadata;
        const configurations = [
          ...state.configurations.slice(0, configAndIndex.index),
          { ...state.configurations[configAndIndex.index], cpmMetadata: action.payload.metadata },
          ...state.configurations.slice(configAndIndex.index + 1),
        ];
        saveToLocalStorage(configurations);
        state.configurations = configurations;
      }
    },
    resetCpmMetadata: (state, action: PayloadAction<string>) => {
      const configAndIndex = tenantConfiguration(state, action.payload);
      if (configAndIndex) {
        state.configurations[configAndIndex.index].cpmMetadata = undefined;
        const configurations = [
          ...state.configurations.slice(0, configAndIndex.index),
          { ...state.configurations[configAndIndex.index], cpmMetadata: undefined },
          ...state.configurations.slice(configAndIndex.index + 1),
        ];
        saveToLocalStorage(configurations);
      }
    },
  },
});
export const tenantConfigurationState = (state: RootState): ConfigurationsState => state.configurationState;

export const selectIsConfigured = (state: RootState) => activeConfiguration(state.configurationState) != null;

export const selectExternalConfigs = (state: RootState) => state.externalConfigurations?.externalConfigs;

export const configurationsNotEmpty = (state: RootState) => state.configurationState.configurations.length > 0;
export const externalConfigAvailable = (state: RootState) => state.externalConfigurations?.externalConfigs != null;
export const selectBackendUrl = (state: RootState) => {
  let activeConf = activeConfiguration(state.configurationState);
  return validateString(activeConf?.url) ? activeConf.url : formatUrl(activeConf?.tenantId);
};
export const selectAuthData = (state: RootState) => activeConfiguration(state.configurationState)?.authData;
export const selectIsAuthenticated = (state: RootState) => {
  const authData = activeConfiguration(state.configurationState)?.authData;
  return authData && validateString(authData.access_token) && Date.now() < authData.expires_at * 1000;
};
export const selectTenantId = (state: RootState) => activeConfiguration(state.configurationState)?.tenantId;
export const selectCpmUser = (state: RootState) => activeConfiguration(state.configurationState)?.cpmUser;
export const selectCpmMetadata = (state: RootState) => activeConfiguration(state.configurationState)?.cpmMetadata;
export const selectCpmUsername = (state: RootState) => activeConfiguration(state.configurationState)?.cpmUser?.userName;
export const selectUserFunctions = (state: RootState) =>
  activeConfiguration(state.configurationState)?.cpmUser?.userFunctions;
export const selectClientId = (state: RootState) => state.configurationState.clientId;
export const selectAzureDefaultSite = (state: RootState) =>
  state.externalConfigurations.externalConfigs.azureDefaultSite;
export const selectSharePointHostName = (state: RootState) =>
  state.externalConfigurations.externalConfigs.sharePointHostName;
export const selectActiveConfiguration = (state: RootState) => activeConfiguration(state.configurationState);
export const selectTenantConfiguration = (state: RootState, tenantId: string): TenantConfiguration | undefined => {
  return tenantConfiguration(tenantConfigurationState(state), tenantId)?.config;
};
export const selectServiceProvider = (state: RootState) => state.configurationState?.serviceProvider;
export const selectIxbrlSettings = (state: RootState) => state.configurationState?.ixbrlSettings;
export const selectProtectionType = (state: RootState) => state.configurationState.protectionType;
export const selectSeahorseDocumentLinked = (state: RootState) => state.configurationState.seahorseDocumentLinked;
export const selectInDesignDocumentLinked = (state: RootState) => state.configurationState.inDesignDocumentLinked;
export const selectUserDocumentCheckout = (state: RootState) => state.configurationState?.userDocumentCheckout;
export const selectAllUserDocumentCheckout = (state: RootState) => state.configurationState?.allUserDocumentCheckout;
export const selectIsSendingIxbrl = (state: RootState) => state.configurationState?.isSendingIxbrl;

const tenantConfiguration = (
  configurationState: ConfigurationsState,
  tenantId: string
): { config: TenantConfiguration; index: number } | undefined => {
  let found: { config: TenantConfiguration; index: number };
  configurationState.configurations.forEach((conf, index) => {
    if (conf.tenantId === tenantId) {
      found = { config: conf, index: index };
    }
  });
  return found;
};

export const {
  updateConfigurationsState,
  confirmConfigurationsState,
  addNewConfiguration,
  bulkImportConfigurations,
  pasteNewConfiguration,
  resetSate,
  deleteConfiguration,
  setEdit,
  updateAuthDataForTenantConf,
  selectConfiguration,
  setDocumentTenantId,
  setClientId,
  setCpmUser,
  resetCpmUser,
  setCpmMetadata,
  resetCpmMetadata,
  setProtectionType,
  setServiceProvider,
  setSeahorseDocumentLinked,
  setIxbrlSettings,
  setIxbrlEnabled,
  setInDesignDocumentLinked,
  setUserDocumentCheckout,
  setAllUserDocumentCheckout,
  setIsSendingIxbrl,
} = slice.actions;
export default slice.reducer;
